import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {PaginationModel, RequestParamModel} from '../models';
import {BaseService} from '../services';

/**
 * Suppliers and their Production House -> Warehouse lead times.
 *
 * All three calls go to gatherco-api-v2 (`supplier` is in BaseService's NODE_JS_API_PATHS list,
 * so it resolves to `environment.nodeApiUrl`). The lead time configured here is the source of
 * truth for a container's APCD, which api-v2 calculates.
 */
@Injectable({
  providedIn: 'root'
})
export class SupplierService {

  constructor(private baseService: BaseService) {
  }

  /**
   * Paged supplier list for the Supplier Management table.
   */
  getSuppliers(requestParamModel: RequestParamModel): Observable<PaginationModel<any>> {
    return this.baseService.get('supplier', requestParamModel)
      .pipe(map<any, any>(response => {
          const supplierList = new PaginationModel<any>();
          supplierList.setHeaders(response.headers);
          supplierList.data = response.body.data as any[];
          return supplierList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * Pulls the supplier list from Unleashed.
   *
   * Resolves to a `{created, updated, unchanged, total}` summary. Configured lead times are not
   * touched by the sync — it writes only the columns Unleashed owns.
   *
   * `skipError` is set so the caller can render the failure inline instead of the global toast:
   * a sync is a deliberate, long-running action and the result belongs next to the button.
   */
  syncSuppliers(): Observable<any> {
    return this.baseService.post('supplier/sync', {}, false, true)
      .pipe(map<any, any>(response => response?.data ?? response),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * Persists a supplier's Production House -> Warehouse lead time.
   *
   * `leadTimeWeeks` is a whole number of weeks, or null to clear it back to "not configured".
   */
  updateLeadTime(id: number, leadTimeWeeks: number | null): Observable<any> {
    return this.baseService.patch(`supplier/${id}`, {lead_time_weeks: leadTimeWeeks})
      .pipe(map<any, any>(response => response?.data ?? response),
        catchError(err => throwError(() => err))
      );
  }
}
